export type SankeyFlow<S, D> = { readonly destination: D; readonly share: number; readonly source: S };

export type SankeyNode<T> = { readonly id: T; readonly share: number; readonly y0: number; readonly y1: number };

export type SankeyRibbon<S, D> = SankeyFlow<S, D> & {
    readonly sourceY0: number;
    readonly sourceY1: number;
    readonly targetY0: number;
    readonly targetY1: number;
};

export type SankeyLayout<S, D> = {
    readonly destinations: readonly SankeyNode<D>[];
    readonly ribbons: readonly SankeyRibbon<S, D>[];
    readonly sources: readonly SankeyNode<S>[];
};

export type SankeyOptions = { readonly gap: number; readonly height: number };

const EMPTY: SankeyLayout<never, never> = { destinations: [], ribbons: [], sources: [] };

const pairKey = (source: string, destination: string): string => `${source}|${destination}`;

const mergeFlows = <S extends string, D extends string>(
    flows: readonly SankeyFlow<S, D>[],
    sourceOrder: readonly S[],
    destinationOrder: readonly D[],
): readonly SankeyFlow<S, D>[] => {
    const merged = new Map<string, SankeyFlow<S, D>>();

    for (const flow of flows) {
        if (flow.share <= 0 || !sourceOrder.includes(flow.source) || !destinationOrder.includes(flow.destination)) {
            continue;
        }

        const key = pairKey(flow.source, flow.destination);
        const existing = merged.get(key);
        merged.set(key, existing === undefined ? flow : { ...existing, share: existing.share + flow.share });
    }

    return [...merged.values()];
};

const totalsInOrder = <T extends string, S extends string, D extends string>(
    order: readonly T[],
    flows: readonly SankeyFlow<S, D>[],
    pick: (flow: SankeyFlow<S, D>) => T,
): readonly { readonly id: T; readonly share: number }[] =>
    order
        .map((id) => ({
            id,
            share: flows.filter((flow) => pick(flow) === id).reduce((sum, flow) => sum + flow.share, 0),
        }))
        .filter((node) => node.share > 0);

const stackNodes = <T>(
    totals: readonly { readonly id: T; readonly share: number }[],
    scale: number,
    { gap, height }: SankeyOptions,
): readonly SankeyNode<T>[] => {
    const used = totals.reduce((sum, node) => sum + node.share * scale, 0) + gap * Math.max(0, totals.length - 1);
    let y = (height - used) / 2;

    return totals.map((node) => {
        const y0 = y;
        const y1 = y0 + node.share * scale;
        y = y1 + gap;

        return { id: node.id, share: node.share, y0, y1 };
    });
};

/**
 * A two-column Sankey: every source node on the left, every destination node on the right, both stacks
 * centred vertically. One scale serves both columns so a ribbon is as wide at its source as at its target.
 * Ribbons leave a source in destination order and arrive at a destination in source order, so they never cross
 * inside a node.
 */
export const layoutSankey = <S extends string, D extends string>(
    flows: readonly SankeyFlow<S, D>[],
    sourceOrder: readonly S[],
    destinationOrder: readonly D[],
    options: SankeyOptions,
): SankeyLayout<S, D> => {
    const merged = mergeFlows(flows, sourceOrder, destinationOrder);
    const total = merged.reduce((sum, flow) => sum + flow.share, 0);

    if (total <= 0) {
        return EMPTY;
    }

    const sourceTotals = totalsInOrder(sourceOrder, merged, (flow) => flow.source);
    const destinationTotals = totalsInOrder(destinationOrder, merged, (flow) => flow.destination);
    const scaleFor = (count: number): number => Math.max(0, options.height - options.gap * (count - 1)) / total;
    const scale = Math.min(scaleFor(sourceTotals.length), scaleFor(destinationTotals.length));
    const sources = stackNodes(sourceTotals, scale, options);
    const destinations = stackNodes(destinationTotals, scale, options);

    const bySource = [...merged].sort(
        (a, b) =>
            sourceOrder.indexOf(a.source) - sourceOrder.indexOf(b.source) ||
            destinationOrder.indexOf(a.destination) - destinationOrder.indexOf(b.destination),
    );
    const byDestination = [...merged].sort(
        (a, b) =>
            destinationOrder.indexOf(a.destination) - destinationOrder.indexOf(b.destination) ||
            sourceOrder.indexOf(a.source) - sourceOrder.indexOf(b.source),
    );

    const sourceOffsets = new Map<string, number>();
    const cursorBySource = new Map<S, number>(sources.map((node) => [node.id, node.y0]));

    for (const flow of bySource) {
        const cursor = cursorBySource.get(flow.source) ?? 0;
        sourceOffsets.set(pairKey(flow.source, flow.destination), cursor);
        cursorBySource.set(flow.source, cursor + flow.share * scale);
    }

    const targetOffsets = new Map<string, number>();
    const cursorByDestination = new Map<D, number>(destinations.map((node) => [node.id, node.y0]));

    for (const flow of byDestination) {
        const cursor = cursorByDestination.get(flow.destination) ?? 0;
        targetOffsets.set(pairKey(flow.source, flow.destination), cursor);
        cursorByDestination.set(flow.destination, cursor + flow.share * scale);
    }

    const ribbons = bySource.map((flow) => {
        const key = pairKey(flow.source, flow.destination);
        const sourceY0 = sourceOffsets.get(key) ?? 0;
        const targetY0 = targetOffsets.get(key) ?? 0;
        const thickness = flow.share * scale;

        return { ...flow, sourceY0, sourceY1: sourceY0 + thickness, targetY0, targetY1: targetY0 + thickness };
    });

    return { destinations, ribbons, sources };
};

/**
 * A closed ribbon between two vertical edges: cubic curves along the top and bottom, and quarter turns into the
 * straight ends so a thin flow does not read as a shard. The radius yields to the thinner of the two ends.
 */
export const ribbonPath = (x0: number, x1: number, ribbon: SankeyRibbon<string, string>, cornerRadius = 0): string => {
    const xm = (x0 + x1) / 2;
    const thickness = Math.min(ribbon.sourceY1 - ribbon.sourceY0, ribbon.targetY1 - ribbon.targetY0);
    const r = Math.max(0, Math.min(cornerRadius, thickness / 2, (x1 - x0) / 4));

    if (r === 0) {
        return [
            `M${x0},${ribbon.sourceY0}`,
            `C${xm},${ribbon.sourceY0} ${xm},${ribbon.targetY0} ${x1},${ribbon.targetY0}`,
            `L${x1},${ribbon.targetY1}`,
            `C${xm},${ribbon.targetY1} ${xm},${ribbon.sourceY1} ${x0},${ribbon.sourceY1}`,
            'Z',
        ].join(' ');
    }

    return [
        `M${x0},${ribbon.sourceY0 + r}`,
        `Q${x0},${ribbon.sourceY0} ${x0 + r},${ribbon.sourceY0}`,
        `C${xm},${ribbon.sourceY0} ${xm},${ribbon.targetY0} ${x1 - r},${ribbon.targetY0}`,
        `Q${x1},${ribbon.targetY0} ${x1},${ribbon.targetY0 + r}`,
        `L${x1},${ribbon.targetY1 - r}`,
        `Q${x1},${ribbon.targetY1} ${x1 - r},${ribbon.targetY1}`,
        `C${xm},${ribbon.targetY1} ${xm},${ribbon.sourceY1} ${x0 + r},${ribbon.sourceY1}`,
        `Q${x0},${ribbon.sourceY1} ${x0},${ribbon.sourceY1 - r}`,
        'Z',
    ].join(' ');
};

/**
 * Top edges for labels that want to sit at the given centres but must not overlap: each label yields to the
 * one above, and the whole stack slides up when the last one would leave the frame.
 */
export const stackLabels = (centers: readonly number[], labelHeight: number, height: number): readonly number[] => {
    const tops: number[] = [];

    for (const center of centers) {
        const wanted = center - labelHeight / 2;
        const previous = tops.at(-1);
        tops.push(previous === undefined ? wanted : Math.max(wanted, previous + labelHeight));
    }

    const last = tops.at(-1);
    const overflow = last === undefined ? 0 : Math.max(0, last + labelHeight - height);

    return tops.map((top) => Math.max(0, top - overflow));
};
